import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

interface AdminLoginProps {
  onLogin: () => void;
}

type Step = 'checking' | 'setup' | 'login';

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const [step, setStep] = useState<Step>('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if admin account exists
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/setup-admin-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'check' }),
        });
        const data = await res.json();
        setStep(data.exists ? 'login' : 'setup');
      } catch {
        // Fallback to login if check fails
        setStep('login');
      }
    };
    checkAdmin();
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  // ── SETUP: إنشاء حساب الأدمن الأول ──
  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('يرجى إدخال البريد الإلكتروني'); triggerShake(); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('البريد الإلكتروني غير صحيح'); triggerShake(); return; }
    if (password.length < 8) { setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); triggerShake(); return; }
    if (password !== confirmPassword) { setError('كلمتا المرور غير متطابقتين'); triggerShake(); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/setup-admin-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();

      if (data.error) {
        setError(data.error === 'Admin account already exists' ? 'يوجد حساب أدمن بالفعل' : data.error);
        triggerShake();
        setLoading(false);
        return;
      }

      // Auto login after setup
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        setStep('login');
        setLoading(false);
        return;
      }

      onLogin();
    } catch {
      setError('حدث خطأ، تحقق من الاتصال وحاول مرة أخرى');
      triggerShake();
    }
    setLoading(false);
  };

  // ── LOGIN: تسجيل الدخول ──
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError('يرجى إدخال البريد وكلمة المرور'); triggerShake(); return; }

    setLoading(true);
    setError('');
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError || !data.user) {
        setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
        triggerShake();
        setLoading(false);
        return;
      }

      // Verify it's an admin account
      if (data.user.user_metadata?.role !== 'admin') {
        await supabase.auth.signOut();
        setError('هذا الحساب ليس حساب مدير');
        triggerShake();
        setLoading(false);
        return;
      }

      onLogin();
    } catch {
      setError('حدث خطأ، تحقق من الاتصال وحاول مرة أخرى');
      triggerShake();
    }
    setLoading(false);
  };

  // ── CHECKING ──
  if (step === 'checking') {
    return (
      <div className="min-h-screen bg-[#f5f5f3] flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">جاري التحقق...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f3] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-sm border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="bg-[#1a1a1a] px-8 py-8 flex flex-col items-center gap-3">
          <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-2xl">
            <i className="ri-shield-keyhole-line text-white text-2xl"></i>
          </div>
          <div className="text-center">
            <h1 className="text-lg font-bold text-white">
              {step === 'setup' ? 'إعداد حساب المدير' : 'لوحة التحكم'}
            </h1>
            <p className="text-sm text-white/50 mt-1">
              {step === 'setup'
                ? 'أنشئ حساب المدير للمرة الأولى'
                : 'سجّل دخولك للمتابعة'}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="px-8 py-7">
          {step === 'setup' && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
              <i className="ri-information-line text-amber-600 text-sm mt-0.5 flex-shrink-0"></i>
              <p className="text-amber-700 text-xs leading-relaxed">
                هذه المرة الأولى — أنشئ بريد وكلمة مرور للوحة التحكم. احتفظ بهما في مكان آمن.
              </p>
            </div>
          )}

          <form
            onSubmit={step === 'setup' ? handleSetup : handleLogin}
            className={`flex flex-col gap-4 transition-transform ${shake ? 'animate-shake' : ''}`}
          >
            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">البريد الإلكتروني</label>
              <div className="relative">
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="admin@example.com"
                  autoComplete="email"
                  className={`w-full border ${error && !password ? 'border-red-300 bg-red-50' : 'border-gray-200'} rounded-xl pr-9 pl-4 py-3 text-sm outline-none focus:border-[#1a1a1a] transition-colors`}
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">
                {step === 'setup' ? 'كلمة المرور (8 أحرف على الأقل)' : 'كلمة المرور'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  autoComplete={step === 'setup' ? 'new-password' : 'current-password'}
                  className={`w-full border ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'} rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1a1a1a] transition-colors pl-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`text-sm ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                </button>
              </div>
              {/* Password strength for setup */}
              {step === 'setup' && password && (
                <div className="flex items-center gap-1.5 mt-1">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        password.length >= i * 3
                          ? password.length >= 12 ? 'bg-green-500'
                            : password.length >= 8 ? 'bg-amber-400'
                            : 'bg-red-400'
                          : 'bg-gray-200'
                      }`}
                    ></div>
                  ))}
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {password.length >= 12 ? 'قوية جداً' : password.length >= 8 ? 'جيدة' : 'ضعيفة'}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password (setup only) */}
            {step === 'setup' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-500">تأكيد كلمة المرور</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                    placeholder="أعد كتابة كلمة المرور"
                    autoComplete="new-password"
                    className={`w-full border ${error && confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'} rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1a1a1a] transition-colors pl-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={`text-sm ${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  </button>
                </div>
                {confirmPassword && password === confirmPassword && (
                  <p className="text-green-500 text-xs flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> كلمتا المرور متطابقتان
                  </p>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
                <p className="text-red-600 text-xs">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-white rounded-xl py-3 text-sm font-bold hover:bg-[#333] transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer mt-1"
            >
              {loading
                ? <><i className="ri-loader-4-line animate-spin"></i> جاري المعالجة...</>
                : step === 'setup'
                ? <><i className="ri-shield-check-line"></i> إنشاء الحساب والدخول</>
                : <><i className="ri-login-box-line"></i> تسجيل الدخول</>
              }
            </button>
          </form>

          {/* Security note */}
          <div className="mt-5 flex items-start gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
            <i className="ri-lock-line text-gray-400 text-sm mt-0.5 flex-shrink-0"></i>
            <p className="text-gray-400 text-[11px] leading-relaxed">
              {step === 'setup'
                ? 'بيانات الدخول محمية بـ Supabase Auth — لا تُحفظ في قاعدة البيانات مباشرة'
                : 'الجلسة محمية بـ Supabase Auth وتنتهي تلقائياً عند الخروج'}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.4s ease; }
      `}</style>
    </div>
  );
}
